import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

export interface DataImport {
  id: string
  entity_type: "companies" | "contacts"
  file_name: string
  status: "pending" | "processing" | "completed" | "failed"
  total_rows: number
  imported_rows: number
  failed_rows: number
  field_mapping?: Record<string, string>
  errors_log?: Array<{ row: number; message: string }>
  csv_headers?: string[]
  csv_preview?: string[][]
  user_id: string
  created_at: string
  updated_at: string
}

export interface ImportField {
  key: string
  label: string
  required: boolean
  custom_field?: boolean
}

export interface PreviewResponse {
  data: DataImport
  headers: string[]
  preview: string[][]
  total_rows: number
  available_fields: ImportField[]
}

export function useDataImports() {
  return useQuery({
    queryKey: ["data_imports"],
    queryFn: () => api.get<{ data: DataImport[] }>("/api/v1/data_imports"),
  })
}

export function useDataImport(id: string | undefined) {
  return useQuery({
    queryKey: ["data_imports", id],
    queryFn: () => api.get<{ data: DataImport }>(`/api/v1/data_imports/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status
      if (status === "processing") return 2000
      return false
    },
  })
}

export function useUploadCsv() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, entityType }: { file: File; entityType: string }) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("entity_type", entityType)
      return api.upload<PreviewResponse>("/api/v1/data_imports", formData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data_imports"] })
    },
  })
}

export function useExecuteImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      importId,
      fieldMapping,
    }: {
      importId: string
      fieldMapping: Record<string, string>
    }) =>
      api.post<{ data: DataImport }>(`/api/v1/data_imports/${importId}/execute`, {
        field_mapping: fieldMapping,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data_imports"] })
      queryClient.invalidateQueries({ queryKey: ["companies"] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    },
  })
}
